import React from "react";
import {
  Box,
  Typography,
  useTheme,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  Article,
  Campaign,
  LocalOffer,
  Feedback,
  ThumbUp,
  Visibility,
  Star,
} from "@mui/icons-material";

const WidgetHeader = ({ title, icon: Icon }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      {Icon && <Icon sx={{ color: theme.palette.text.secondary }} />}
      <Typography variant="h6" fontWeight="bold">
        {title}
      </Typography>
    </Box>
  );
};

const StatRow = ({ icon: Icon, label, value, subtext, color }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar
          sx={{
            bgcolor: color || theme.palette.primary.light,
            color: theme.palette.getContrastText(
              color || theme.palette.primary.light
            ),
            width: 32,
            height: 32,
          }}
        >
          <Icon fontSize="small" />
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {label}
          </Typography>
          {subtext && (
            <Typography variant="caption" color="text.secondary">
              {subtext}
            </Typography>
          )}
        </Box>
      </Box>
      <Typography variant="h6" fontWeight="bold">
        {value}
      </Typography>
    </Box>
  );
};

const ContentAnalytics = ({ data }) => {
  const theme = useTheme();
  const counts = data?.counts || {};
  const engagement = data?.engagement || {};
  const topBlogs = data?.topBlogs || [];
  const recentAnnouncements = data?.recentAnnouncements || [];
  const recentOffers = data?.recentOffers || [];

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        height: "100%",
      }}
    >
      <WidgetHeader title="Content & Activity" icon={Article} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Overview
            </Typography>
            <StatRow
              icon={Campaign}
              label="Active Announcements"
              value={counts.announcements || 0}
              color={theme.palette.warning.light}
            />
            <StatRow
              icon={Article}
              label="Blogs Published"
              value={counts.blogs || 0}
              subtext={`${engagement.views || 0} Views`}
              color={theme.palette.info.light}
            />
            <StatRow
              icon={LocalOffer}
              label="Active Offers"
              value={counts.offers || 0}
              color={theme.palette.success.light}
            />
            <StatRow
              icon={Feedback}
              label="Feedback Received"
              value={counts.feedback || 0}
              color={theme.palette.error.light}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Top Performing Blogs
          </Typography>
          <List dense disablePadding>
            {topBlogs.map((blog, index) => (
              <ListItem
                key={index}
                disableGutters
                sx={{ borderBottom: `1px dashed ${theme.palette.divider}` }}
              >
                <ListItemText
                  primary={blog.title}
                  primaryTypographyProps={{
                    noWrap: true,
                    fontWeight: "medium",
                  }}
                  secondary={
                    <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Visibility
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        />
                        <Typography variant="caption">{blog.views}</Typography>
                      </Box>
                      {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ThumbUp sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                <Typography variant="caption">{blog.likes}</Typography>
                                            </Box> */}
                    </Box>
                  }
                />
                {index === 0 && (
                  <Star fontSize="small" sx={{ color: "#FFD700" }} />
                )}
              </ListItem>
            ))}
            {topBlogs.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No blogs yet
              </Typography>
            )}
          </List>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Active Announcements
            </Typography>
            <List dense disablePadding>
              {recentAnnouncements.map((item, index) => (
                <ListItem
                  key={index}
                  disableGutters
                  sx={{
                    py: 0.5,
                    borderBottom: `1px dashed ${theme.palette.divider}`,
                  }}
                >
                  <ListItemText
                    primary={item.name || item.title}
                    secondary={
                      <React.Fragment>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Page: {item.pageName || "All"} •{" "}
                          {new Date(item.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.primary"
                          display="block"
                          noWrap
                        >
                          {item.text}
                        </Typography>
                      </React.Fragment>
                    }
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: "medium",
                    }}
                  />
                </ListItem>
              ))}
              {recentAnnouncements.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No active announcements
                </Typography>
              )}
            </List>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Active Offers
            </Typography>
            <List dense disablePadding>
              {recentOffers.map((item, index) => (
                <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={item.title || item.name}
                    secondary={
                      item.endTime
                        ? `Ends: ${new Date(item.endTime).toLocaleDateString()}`
                        : "No Expiration"
                    }
                    primaryTypographyProps={{ variant: "body2", noWrap: true }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItem>
              ))}
              {recentOffers.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No active offers
                </Typography>
              )}
            </List>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContentAnalytics;
